import {
  Alert,
  Badge,
  Button,
  Group,
  NumberInput,
  Popover,
  Select,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { ApiError } from '../../api/errors';
import { listKey } from '../../api/queryKeys';
import { currencySymbol, formatMoney, toMajorUnits, toMinorUnits } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';

/*
 * THE MENU'S OWN LISTS, and never a regular's negotiated one.
 *
 * This panel answers "where does this dish get its price?" and offers to put one somewhere. A list
 * belonging to one named person is not an answer to that question — and it would have been a
 * dangerous one: `alreadyOn` below picks the lowest-priority list that already prices the item, so
 * a size the everyday card prices NOWHERE but a regular's arrangement does would open this panel on
 * HIS list, showing his negotiated price as the item's price and saving an edit straight onto his
 * arrangement from the catalog screen. What one person pays is changed on that person's screen.
 */
const LISTS_PATH = '/admin/v1/pricing/price-lists?scope=menu';
const ENTRIES_PATH = '/admin/v1/pricing/price-entries';

interface Resolved {
  currency: 'USD' | 'HNL';
  unitPriceMinor: number;
}

interface PriceList {
  id: string;
  name: string;
  priority: number;
  channelId: string | null;
  schedule: unknown;
  active: boolean;
}

interface PriceEntry {
  id: string;
  priceListId: string;
  usdMinor: number | null;
  hnlMinor: number | null;
  adjustmentBp: number | null;
  qtyTiers: unknown;
}

/**
 * What a price can be put on. The same three the pricing engine resolves against.
 *
 * `modifierOption` was always one of them on the API and had no screen: a happy-hour list that
 * makes the extra shot free is a price entry against a choice, and the only way to write one was a
 * database console. It arrives here because the recipe screen now shows what an extra COSTS, and a
 * cost with no price beside it is the half-fact this component exists to close.
 */
export type PricedTarget = 'variant' | 'bundle' | 'modifierOption';

/**
 * The list a price belongs on unless somebody says otherwise.
 *
 * "Everyday" is not a flag on the table, it is what a list with no hours, no channel and the
 * lowest priority MEANS — the one the register falls back to when no special is running. Pricing a new
 * size onto Happy Hour by accident would leave it unringable for twenty-one hours a day, which is
 * exactly the failure this whole control exists to prevent, so the default is worked out rather
 * than being whichever list the API happened to return first.
 */
function everydayList(lists: PriceList[]): PriceList | undefined {
  const usable = lists.filter((l) => l.active !== false);
  const plain = usable.filter((l) => !l.schedule && !l.channelId);
  return (plain.length ? plain : usable).slice().sort((a, b) => a.priority - b.priority)[0];
}

/**
 * Putting a price on the thing you are looking at.
 *
 * A size and a combo are each priced on their own, and the price lived on a different screen
 * behind a list picker and a target picker — so the journey from "I have added a Pitcher" to "a
 * server can ring up a Pitcher" was three screens and about ten clicks, ending in a picker holding
 * every size on the menu. Nothing about that journey needed to be asked: the size is already the
 * subject of the row this sits in.
 *
 * It edits the entry that is already there rather than adding beside it. Two prices for one drink
 * on one list is a genuine hazard — the register takes one of them and nobody can say which.
 *
 * Exported because the recipe screen sets prices too: a recipe's whole point is the margin, and a
 * margin that reads wrong is fixed by changing the price of the dish, not by re-opening the menu
 * screens. A second editor written over there would be a second set of rules about which list a new
 * price lands on, which is the mistake this one exists to prevent.
 */
export function PriceEditor({
  targetType,
  targetId,
  name,
  onDone,
}: {
  targetType: PricedTarget;
  targetId: string;
  name?: string;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const subject =
    name?.trim() ||
    (targetType === 'bundle' ? 'This combo' : targetType === 'modifierOption' ? 'This extra' : 'This size');

  const lists = useQuery({
    queryKey: listKey(LISTS_PATH),
    queryFn: () => rawApi<PriceList[]>('GET', LISTS_PATH),
    staleTime: 60_000,
  });
  const entriesQuery = { targetType, targetId };
  const entries = useQuery({
    queryKey: listKey(ENTRIES_PATH, entriesQuery),
    queryFn: () =>
      rawApi<PriceEntry[]>('GET', `${ENTRIES_PATH}?targetType=${targetType}&targetId=${targetId}`),
  });

  const [chosen, setChosen] = useState<string | null>(null);
  const [amounts, setAmounts] = useState<{ hnl: number | string; usd: number | string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const available = (lists.data ?? []).filter((l) => l.active !== false);
  const priced = entries.data ?? [];
  // Where the price already is, if it is anywhere: correcting one is far more common than adding
  // a first, and a manager who opens this to fix an everyday price should not have to find it.
  const alreadyOn = available
    .filter((l) => priced.some((e) => e.priceListId === l.id))
    .sort((a, b) => a.priority - b.priority)[0];
  const listId = chosen ?? alreadyOn?.id ?? everydayList(available)?.id ?? null;
  const entry = priced.find((e) => e.priceListId === listId) ?? null;

  // Seeded from whichever list is showing, and re-seeded when it changes, so switching to Happy
  // Hour shows the happy-hour price rather than carrying the everyday one over onto it.
  const shown = amounts ?? { hnl: toMajorUnits(entry?.hnlMinor), usd: toMajorUnits(entry?.usdMinor) };
  const pickList = (next: string | null) => {
    setChosen(next);
    setAmounts(null);
    setError(null);
  };

  const save = useMutation({
    mutationFn: async () => {
      const hnlMinor = toMinorUnits(shown.hnl) ?? null;
      const usdMinor = toMinorUnits(shown.usd) ?? null;
      if (entry) {
        return rawApi('PATCH', `${ENTRIES_PATH}/${entry.id}`, { hnlMinor, usdMinor });
      }
      return rawApi('POST', ENTRIES_PATH, {
        priceListId: listId,
        targetType,
        targetId,
        hnlMinor,
        usdMinor,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['rings-up-at', targetType, targetId] });
      void queryClient.invalidateQueries({ queryKey: listKey(ENTRIES_PATH, entriesQuery) });
      // The Prices screen lists the same entries per list; without this it keeps yesterday's.
      void queryClient.invalidateQueries({ queryKey: listKey(`${LISTS_PATH}/${listId}/entries`) });
      const amount =
        toMinorUnits(shown.hnl) != null
          ? formatMoney(toMinorUnits(shown.hnl), 'HNL')
          : formatMoney(toMinorUnits(shown.usd), 'USD');
      notifyDone(
        'Priced',
        `${subject} rings up at ${amount}. Send the menu to the registers when you are ready for staff to see it.`,
      );
      onDone();
    },
    onError: (err) => notifyError(err, 'set this price'),
  });

  const submit = () => {
    if (!listId) return setError('Add a price list on the Prices screen first');
    if (toMinorUnits(shown.hnl) == null && toMinorUnits(shown.usd) == null) {
      return setError('Enter a lempira price, a dollar price, or both');
    }
    setError(null);
    save.mutate();
  };

  if (lists.isError || entries.isError) {
    return (
      <Alert color="red" title="Could not open this">
        The price lists could not be reached. Nothing has been lost — this is only a display problem.
      </Alert>
    );
  }

  if (!lists.isLoading && available.length === 0) {
    return (
      <Alert color="blue" title="No price lists yet">
        Prices hang off a list. Add your everyday list on the Prices screen, then price this here.
      </Alert>
    );
  }

  const elsewhere = priced.filter((e) => e.priceListId !== listId);

  return (
    <Stack gap="sm" w={300}>
      <Select
        label="On which price list"
        data={available
          .slice()
          .sort((a, b) => a.priority - b.priority)
          .map((l) => ({
            value: l.id,
            label: priced.some((e) => e.priceListId === l.id) ? `${l.name} — priced` : l.name,
          }))}
        value={listId}
        onChange={pickList}
        allowDeselect={false}
        disabled={lists.isLoading || entries.isLoading}
        searchable={available.length > 8}
        comboboxProps={{ withinPortal: true }}
      />
      <Group gap="xs" grow>
        <NumberInput
          label="Lempiras"
          prefix={`${currencySymbol('HNL')} `}
          value={shown.hnl}
          onChange={(v) => setAmounts({ ...shown, hnl: v })}
          decimalScale={2}
          thousandSeparator=","
          hideControls
          min={0}
          // Until we know what is already priced, typing would be typing over an unread number.
          disabled={entries.isLoading}
        />
        <NumberInput
          label="Dollars"
          prefix={`${currencySymbol('USD')} `}
          value={shown.usd}
          onChange={(v) => setAmounts({ ...shown, usd: v })}
          decimalScale={2}
          thousandSeparator=","
          hideControls
          min={0}
          disabled={entries.isLoading}
        />
      </Group>
      <Text size="xs" c="dimmed">
        Set one or both. Each stands on its own — the register never converts one into the other.
      </Text>

      {/* A size can be priced on several lists, and changing the everyday price while happy hour
          quietly stays at the old number is a mistake nobody sees until Friday evening. */}
      {elsewhere.length > 0 && (
        <Text size="xs" c="dimmed">
          Also priced on{' '}
          {elsewhere
            .map((e) => {
              const list = available.find((l) => l.id === e.priceListId);
              const amount =
                e.hnlMinor != null ? formatMoney(e.hnlMinor, 'HNL') : formatMoney(e.usdMinor, 'USD');
              return `${list?.name ?? 'another list'} at ${amount}`;
            })
            .join(', ')}
        </Text>
      )}

      {error && (
        <Text size="sm" c="red">
          {error}
        </Text>
      )}
      <Group justify="flex-end" gap="xs">
        <Button size="xs" variant="default" onClick={onDone} disabled={save.isPending}>
          Cancel
        </Button>
        <Button size="xs" loading={save.isPending} onClick={submit} disabled={entries.isLoading}>
          {entry ? 'Save price' : 'Set price'}
        </Button>
      </Group>
    </Stack>
  );
}

/**
 * What a guest would be charged for this, worked out the way the register does it — and where that
 * price is set.
 *
 * The menu screens and the price screens had no thread between them: a size or a combo could be
 * built, published to every register, and have no price on any list — and nothing said so until a
 * server stood at the register with a guest waiting and could not ring it up. The gap was easiest
 * to fall into exactly where it hurts, on a size added to an existing drink and on a combo built
 * for the weekend, because both look finished the moment they are named.
 *
 * Showing the gap was only half of it. The badge said "No price yet" beside a button that went
 * nowhere, and the fix was three screens away, so this cell is now the place the price is set as
 * well as the place it is read.
 *
 * Asked of the pricing engine rather than read off a table, so what it shows is what the register
 * would charge tonight — happy hour, quantity deals and all.
 */
export function RingsUpAt({
  targetType,
  targetId,
  name,
}: {
  targetType: PricedTarget;
  targetId: string;
  /** What this is called, for the confirmation after a price is set. */
  name?: string;
}) {
  const [editing, setEditing] = useState(false);
  const price = useQuery({
    queryKey: ['rings-up-at', targetType, targetId],
    queryFn: () =>
      rawApi<Resolved>('POST', '/admin/v1/pricing/resolve-preview', {
        targetType,
        targetId,
        qty: 1,
        at: new Date().toISOString(),
        // The engine answers in whichever money the thing is actually priced in and says which it
        // used, so asking in lempiras never converts a dollar price — it just falls back to it.
        pricingCurrency: 'HNL',
      }),
    enabled: Boolean(targetId),
    // A missing price is a real answer, not a failure to retry.
    retry: false,
    staleTime: 60_000,
  });

  if (price.isLoading) return null;

  // Only the engine's own "nothing prices this" answer earns the warning. A dropped connection
  // must never be reported as an unpriced item, or the first fix would be to price something that
  // already has a price.
  const unpriced = price.error instanceof ApiError && price.error.status === 404;
  const label = price.data ? (
    <Text size="sm">{formatMoney(price.data.unitPriceMinor, price.data.currency)}</Text>
  ) : unpriced ? (
    <Badge color="orange" variant="light" style={{ cursor: 'pointer' }}>
      Set a price
    </Badge>
  ) : (
    <Text c="dimmed">—</Text>
  );

  // A price that could not be reached is the one case with nothing to edit: we do not know whether
  // there is an entry to correct, and opening the editor would offer to add a second one.
  if (!price.data && !unpriced) return label;

  return (
    <Popover
      opened={editing}
      onChange={setEditing}
      position="bottom-start"
      withArrow
      shadow="md"
      trapFocus
      withinPortal
    >
      <Popover.Target>
        <UnstyledButton
          onClick={() => setEditing((o) => !o)}
          title={price.data ? 'Change this price' : 'Put a price on this'}
          style={{ borderBottom: '1px dashed var(--mantine-color-dimmed)' }}
        >
          {label}
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown>
        <PriceEditor
          targetType={targetType}
          targetId={targetId}
          name={name}
          onDone={() => setEditing(false)}
        />
      </Popover.Dropdown>
    </Popover>
  );
}
